import { DexieArquivos, DexieStore, EnvioDeArquivos, EquiviewDb, SyncEngine, deviceId, type SyncReport } from '@equiview/sync';
import { useSyncExternalStore } from 'react';
import { supabase } from './supabase.js';
import { SupabaseTransport } from './transport.js';

/** Banco local do dispositivo. Vive enquanto o app estiver instalado. */
export const db = new EquiviewDb();

export const DEVICE_ID = deviceId(window.localStorage);

export const store = new DexieStore(db, DEVICE_ID);

export const engine = new SyncEngine(store, new SupabaseTransport(supabase));

/** Fotos e videos esperando para subir ao Storage (bucket `midia`, ver 0009). */
export const arquivos = new DexieArquivos(db);

export const envioDeArquivos = new EnvioDeArquivos(
  arquivos,
  async (caminho, blob, mime) => {
    const { error } = await supabase.storage.from('midia').upload(caminho, blob, { contentType: mime, upsert: false });
    if (error) throw new Error(error.message);
  },
  // O caminho vai pelo sync, como escrita parcial da linha de `media`.
  (mediaId, patch) => engine.registrar('media', mediaId, patch),
);

// --- "Sincronizando agora", para o indicador e a tela de sincronizacao -------

let sincronizando = false;
const ouvintes = new Set<() => void>();
const avisar = () => ouvintes.forEach((o) => o());

/**
 * Uma rodada de sincronizacao. O resultado fica gravado no estado do sync
 * (`lastErrors`, `lastSyncAt`), que a tela le; aqui so se publica o "rodando".
 */
/**
 * No primeiro login, liga o usuario a pessoa que o admin cadastrou com o mesmo
 * e-mail (vincular_minha_pessoa, 0011). A linha da pessoa chega no pull seguinte,
 * e com ela o papel. Uma vez ligado, nao precisa repetir.
 */
let vinculado = false;
async function vincularPessoa() {
  if (vinculado) return;
  const { data, error } = await supabase.rpc('vincular_minha_pessoa');
  if (!error && data) vinculado = true;
}

export async function sincronizarAgora(): Promise<SyncReport> {
  sincronizando = true;
  avisar();
  try {
    await vincularPessoa().catch(() => undefined);
    const relatorio = await engine.sync();
    // Arquivos depois das linhas: o registro de cada `media` ja esta no servidor
    // (ou na frente dele na fila) quando o caminho do arquivo for gravado.
    const envio = await envioDeArquivos.enviarPendentes();
    // O caminho dos arquivos que subiram volta ao servidor ja nesta rodada.
    if (envio.enviados > 0) return await engine.sync();
    return relatorio;
  } finally {
    sincronizando = false;
    avisar();
  }
}

export function useSincronizando(): boolean {
  return useSyncExternalStore(
    (o) => {
      ouvintes.add(o);
      return () => ouvintes.delete(o);
    },
    () => sincronizando,
  );
}

/**
 * Sincroniza quando ha rede, sem bloquear o uso.
 *
 * A janela de rede no campo e curta e imprevisivel — um sinal no caminho entre
 * dois haras, o wifi da pousada a noite. Por isso: ao voltar o `online`, ao
 * reabrir o app, e num intervalo folgado. Nunca com o usuario esperando.
 */
export function startOpportunisticSync() {
  const tentar = async () => {
    if (!navigator.onLine) return;
    try {
      await sincronizarAgora();
    } catch {
      // O motor ja grava o erro no estado do sync; a tela de sincronizacao o
      // mostra. Aqui nao ha ninguem esperando a resposta.
    }
  };
  const aoVoltar = () => {
    if (document.visibilityState === 'visible') void tentar();
  };

  window.addEventListener('online', tentar);
  document.addEventListener('visibilitychange', aoVoltar);
  const timer = window.setInterval(tentar, 2 * 60 * 1000);
  void tentar();

  return () => {
    window.removeEventListener('online', tentar);
    document.removeEventListener('visibilitychange', aoVoltar);
    window.clearInterval(timer);
  };
}
